// front-end/src/components/dynamic-table/course-detail-table.js

import { isAuthenticated } from "../auth";
import { loadContentForNewElements, setupEditableElements } from '../content-editor-src';
import { apiGet, apiPost } from '../../utils/api';
import { config } from '../../config/app-config';
import { determinePageTitle } from '../../utils/page-utils';

export class CourseDetailTable {
    constructor() {
        this.tableElement = document.querySelector('.spec-table');
        this.rowCount = 0;

        // ВАЖНО: Определяем правильный page title
        this.pageTitle = this.determineTablePageTitle();
        this.tableMetaKey = `${this.pageTitle}-table-meta`;

        console.log(`[CourseDetailTable] Initialized with page: ${this.pageTitle}`);

        if (this.tableElement) {
            this.init();
        }
    }

    determineTablePageTitle() {
        // Сначала проверяем URL
        const urlParams = new URLSearchParams(window.location.search);
        const courseSlug = urlParams.get('slug');

        if (courseSlug) {
            return `Course - ${courseSlug}`;
        }

        // Затем проверяем data-page у body
        const bodyPage = document.body.getAttribute('data-page');
        if (bodyPage && bodyPage.startsWith('Course - ')) {
            return bodyPage;
        }

        // Fallback
        return 'course-detail';
    }

    async init() {
        // Сначала загружаем существующие строки
        await this.loadTableMeta();

        // Делаем существующие ячейки редактируемыми
        await this.makeTableEditable();

        if (isAuthenticated()) {
            this.initializeAdminControls();
        }
    }

    async loadTableMeta() {
        try {
            // Пытаемся загрузить мета-информацию о таблице
            const metaData = await apiGet(`${config.api.paths.content}?page_title=${encodeURIComponent(this.pageTitle)}&tag_id=${this.tableMetaKey}`, { auth: false });

            if (metaData && metaData.content) {
                const meta = JSON.parse(metaData.content);
                const savedRowCount = meta.rowCount || 0;

                // Восстанавливаем сохраненные строки
                for (let i = this.rowCount; i < savedRowCount; i++) {
                    await this.addNewRow(false); // false = не сохранять мета-данные сразу
                }
            }
        } catch (error) {
            console.log('No saved table meta found or error loading:', error);
        }
    }

    async saveTableMeta() {
        if (!isAuthenticated()) return;

        try {
            const metaData = {
                rowCount: this.rowCount
            };

            await apiPost(config.api.paths.content, {
                page_title: this.pageTitle,
                tag_id: this.tableMetaKey,
                tag: this.tableMetaKey,
                content: JSON.stringify(metaData),
                order: 1,
                data_attribute: 'json'
            });

            console.log('Table meta saved:', metaData);
        } catch (error) {
            console.error('Error saving table meta:', error);
        }
    }

    initializeAdminControls() {
        // Проверяем, нет ли уже кнопки
        if (this.tableElement.parentNode.querySelector('.add-table-row-btn')) {
            return;
        }

        const addRowBtn = document.createElement('button');
        addRowBtn.textContent = 'Додати рядок';
        addRowBtn.className = 'add-table-row-btn';
        addRowBtn.style.cssText = `
            margin: 20px 0;
            padding: 10px 20px;
            background-color: #4285f4;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-weight: 500;
            transition: background-color 0.3s;
        `;

        addRowBtn.addEventListener('mouseover', () => {
            addRowBtn.style.backgroundColor = '#3367d6';
        });

        addRowBtn.addEventListener('mouseout', () => {
            addRowBtn.style.backgroundColor = '#4285f4';
        });

        this.tableElement.parentNode.insertBefore(addRowBtn, this.tableElement);

        addRowBtn.addEventListener('click', () => this.addNewRow(true));
    }

    async makeTableEditable() {
        const rows = this.tableElement.querySelectorAll('tr');
        const allParagraphs = [];

        rows.forEach((row, rowIndex) => {
            const cells = row.querySelectorAll('td');
            cells.forEach((cell, cellIndex) => {
                // Пропускаем ячейку с кнопкой удаления
                if (cell.classList.contains('delete-cell')) {
                    return;
                }

                const isName = cellIndex === 0;
                const contentType = isName ? 'name' : 'value';

                // Проверяем, есть ли уже параграф в ячейке
                let paragraph = cell.querySelector('p');

                if (!paragraph) {
                    // Сохраняем текущий контент
                    const currentContent = cell.textContent.trim() || (isName ? 'Назва' : 'Значення');

                    // Очищаем ячейку
                    cell.innerHTML = '';

                    // Создаем параграф
                    paragraph = document.createElement('p');
                    paragraph.textContent = currentContent;

                    // Добавляем стили для параграфа
                    paragraph.style.cssText = 'margin: 0; padding: 0;';

                    cell.appendChild(paragraph);
                }

                // Устанавливаем атрибуты для content editor на параграф
                paragraph.setAttribute('data-content', `table-row-${rowIndex}-${contentType}`);
                paragraph.setAttribute('data-page', this.pageTitle); // ВАЖНО: Используем правильный page title
                paragraph.setAttribute('data-type', 'text');

                // Собираем все параграфы для загрузки контента
                allParagraphs.push(paragraph);
            });

            // Обновляем счетчик строк
            if (rowIndex >= this.rowCount) {
                this.rowCount = rowIndex + 1;
            }

            // Добавляем кнопку удаления если ее нет и пользователь авторизован
            if (isAuthenticated() && !row.querySelector('.delete-cell')) {
                this.addDeleteButton(row, rowIndex);
            }
        });

        // ВАЖНО: Загружаем контент для ВСЕХ пользователей, не только авторизованных!
        if (allParagraphs.length > 0) {
            console.log(`Loading content for ${allParagraphs.length} table cells...`);
            try {
                await loadContentForNewElements(allParagraphs);
                console.log('Table content loaded successfully');
            } catch (error) {
                console.error('Error loading table content:', error);
            }
        }

        // Добавляем кнопки редактирования ТОЛЬКО для авторизованных
        if (isAuthenticated()) {
            // Небольшая задержка чтобы убедиться что контент загружен
            setTimeout(() => {
                setupEditableElements();
            }, 100);
        }
    }

    addDeleteButton(row, rowIndex) {
        const deleteCell = document.createElement('td');
        deleteCell.className = 'delete-cell';
        deleteCell.style.cssText = `
            padding: 8px;
            border: none;
            background: transparent;
            width: 50px;
            text-align: center;
            vertical-align: middle;
        `;

        const deleteBtn = document.createElement('button');
        deleteBtn.className = 'delete-row-btn';
        deleteBtn.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 5L15 15M5 15L15 5" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
        `;
        deleteBtn.title = 'Видалити рядок';
        deleteBtn.style.cssText = `
            background-color: #ff4d4d;
            color: white;
            border: none;
            border-radius: 50%;
            width: 32px;
            height: 32px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            position: relative;
            z-index: 5;
        `;

        deleteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.deleteRow(row, rowIndex);
        });

        deleteBtn.addEventListener('mouseover', () => {
            deleteBtn.style.backgroundColor = '#ff3333';
            deleteBtn.style.transform = 'scale(1.1)';
            deleteBtn.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
        });

        deleteBtn.addEventListener('mouseout', () => {
            deleteBtn.style.backgroundColor = '#ff4d4d';
            deleteBtn.style.transform = 'scale(1)';
            deleteBtn.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
        });

        deleteCell.appendChild(deleteBtn);
        row.appendChild(deleteCell);
    }

    async addNewRow(shouldSaveMeta = true) {
        const newRow = document.createElement('tr');

        // Создаем ячейки с параграфами внутри
        const nameCell = document.createElement('td');
        nameCell.className = 'spec-name';

        const nameParagraph = document.createElement('p');
        nameParagraph.setAttribute('data-content', `table-row-${this.rowCount}-name`);
        nameParagraph.setAttribute('data-page', this.pageTitle); // ВАЖНО: Используем правильный page title
        nameParagraph.setAttribute('data-type', 'text');
        nameParagraph.textContent = 'Нова назва';
        nameParagraph.style.cssText = 'margin: 0; padding: 0;';

        nameCell.appendChild(nameParagraph);

        const valueCell = document.createElement('td');
        valueCell.className = 'spec-value';

        const valueParagraph = document.createElement('p');
        valueParagraph.setAttribute('data-content', `table-row-${this.rowCount}-value`);
        valueParagraph.setAttribute('data-page', this.pageTitle); // ВАЖНО: Используем правильный page title
        valueParagraph.setAttribute('data-type', 'text');
        valueParagraph.textContent = 'Нове значення';
        valueParagraph.style.cssText = 'margin: 0; padding: 0;';

        valueCell.appendChild(valueParagraph);

        newRow.appendChild(nameCell);
        newRow.appendChild(valueCell);

        // Добавляем кнопку удаления для админов
        if (isAuthenticated()) {
            this.addDeleteButton(newRow, this.rowCount);
        }

        this.tableElement.appendChild(newRow);
        this.rowCount++;

        // Загружаем контент для новых параграфов (для ВСЕХ пользователей)
        const newParagraphs = newRow.querySelectorAll('p[data-content]');
        if (window.loadContentForNewElements) {
            await loadContentForNewElements(newParagraphs);
        }

        // Добавляем кнопки редактирования ТОЛЬКО если пользователь авторизован
        if (isAuthenticated()) {
            setupEditableElements();
        }

        // Сохраняем мета-информацию о таблице
        if (shouldSaveMeta) {
            await this.saveTableMeta();
        }

        console.log(`Added new row. Total rows: ${this.rowCount}`);
    }

    async deleteRow(rowElement, rowIndex) {
        if (confirm('Ви впевнені, що хочете видалити цей рядок?')) {
            // Добавляем анимацию исчезновения
            rowElement.style.transition = 'opacity 0.3s ease';
            rowElement.style.opacity = '0';

            setTimeout(async () => {
                rowElement.remove();

                // Перенумеровываем оставшиеся строки
                this.renumberRows();

                // Сохраняем обновленную мета-информацию
                await this.saveTableMeta();
            }, 300);
        }
    }

    renumberRows() {
        const rows = this.tableElement.querySelectorAll('tr');
        this.rowCount = rows.length;

        rows.forEach((row, index) => {
            // Обновляем data-content для параграфов в ячейках
            const paragraphs = row.querySelectorAll('p[data-content]');
            paragraphs.forEach((paragraph, cellIndex) => {
                const isName = cellIndex === 0;
                const contentType = isName ? 'name' : 'value';
                const oldContent = paragraph.getAttribute('data-content');
                const newContent = `table-row-${index}-${contentType}`;

                // Обновляем атрибут data-content
                paragraph.setAttribute('data-content', newContent);

                console.log(`Updated paragraph content from ${oldContent} to ${newContent}`);
            });

            // Обновляем обработчик для кнопки удаления
            const deleteBtn = row.querySelector('.delete-row-btn');
            if (deleteBtn) {
                // Удаляем старый обработчик
                const newDeleteBtn = deleteBtn.cloneNode(true);
                deleteBtn.parentNode.replaceChild(newDeleteBtn, deleteBtn);

                // Добавляем новый обработчик с правильным индексом
                newDeleteBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.deleteRow(row, index);
                });

                newDeleteBtn.addEventListener('mouseover', () => {
                    newDeleteBtn.style.backgroundColor = '#ff3333';
                    newDeleteBtn.style.transform = 'scale(1.1)';
                    newDeleteBtn.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                });

                newDeleteBtn.addEventListener('mouseout', () => {
                    newDeleteBtn.style.backgroundColor = '#ff4d4d';
                    newDeleteBtn.style.transform = 'scale(1)';
                    newDeleteBtn.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                });
            }
        });
    }
}